import { type ComponentPropsWithoutRef } from 'react';
import { DatatableRow, TableRow } from '@monksflow/monks-ui';
import { motion } from 'motion/react';
import { type StripMotionCollisions, useStaggerEntrance } from '../staggerEntrance';

// `TableRow` and `DatatableRow` are both `forwardRef<HTMLTableRowElement>`
// in monks-ui source, so `motion.create` wraps them cleanly. We create the
// motion components ONCE at module scope — calling `motion.create` in
// render would spawn a new component on every render and break motion's
// reconciliation (identity change → animation restarts, ref warnings).
const MotionTableRow = motion.create(TableRow);
const MotionDatatableRow = motion.create(DatatableRow);

/** Props accepted when rendering on top of the monks-ui `Table` row. */
type TableVariantProps = StripMotionCollisions<ComponentPropsWithoutRef<typeof TableRow>> & {
  variant?: 'table';
};

/**
 * Props accepted when rendering on top of the monks-ui `Datatable` row —
 * exposes the `interactive` + `state` variant surface.
 */
type DatatableVariantProps = StripMotionCollisions<
  ComponentPropsWithoutRef<typeof DatatableRow>
> & {
  variant: 'datatable';
};

export type AnimatedTableRowProps = (TableVariantProps | DatatableVariantProps) & {
  /** Position in the list — drives the staggered entrance delay. */
  index: number;
};

/**
 * Table row with a staggered "results animate in" entrance.
 *
 * Drop-in replacement for the underlying monks-ui row inside a `.map()`:
 * each row fades and rises in slightly after the previous one. Honors
 * `prefers-reduced-motion` by rendering the plain, static row.
 *
 * Composes ONE of two monks-ui rows depending on `variant`:
 *   - `variant="table"` (DEFAULT) — `TableRow` (monks-ui `Table` surface).
 *     `<TableBody>`'s `[&_tr]:hover:bg-accent` selector matches this row,
 *     and its `border-b` divider is drawn correctly.
 *   - `variant="datatable"` — `DatatableRow` (monks-ui `Datatable` surface).
 *     Exposes the `interactive` + `state` variant props for the 4 refactored
 *     tables (Briefs/Folders/BriefTemplates/Projects rows).
 *
 * Migration guidance: pass `variant="datatable"` when rendering under
 * `<Datatable>`. Under `<Table>`, omit `variant` (or pass `variant="table"`
 * explicitly for clarity).
 */
export function AnimatedTableRow(props: AnimatedTableRowProps) {
  const { shouldReduceMotion, transition } = useStaggerEntrance(props.index);

  if (props.variant === 'datatable') {
    const { index: _index, variant: _variant, children, ...rest } = props;
    // `void` statements silence @typescript-eslint/no-unused-vars for the
    // destructured discards — the project's rule has no varsIgnorePattern,
    // so an underscore prefix alone isn't enough. Cheap and explicit.
    void _index;
    void _variant;

    if (shouldReduceMotion) {
      return <DatatableRow {...rest}>{children}</DatatableRow>;
    }

    return (
      <MotionDatatableRow
        {...rest}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition}
      >
        {children}
      </MotionDatatableRow>
    );
  }

  // Default variant: monks-ui `TableRow` (no interactive/state props).
  const { index: _index, variant: _variant, children, ...rest } = props;
  void _index;
  void _variant;

  if (shouldReduceMotion) {
    return <TableRow {...rest}>{children}</TableRow>;
  }

  return (
    <MotionTableRow
      {...rest}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={transition}
    >
      {children}
    </MotionTableRow>
  );
}

AnimatedTableRow.displayName = 'AnimatedTableRow';
